/*
 * -----------------------------------------------------------------------
 *  <copyright file="editable-grid/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragAndDrop } from '@progress/kendo-react-common';
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import cn from 'clsx';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
    StudyOverviewTableConfigurationColumnConfigurations,
    // eslint-disable-next-line max-len
} from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-study-site-overview-config/edit-study-site-overview-config.interface';
import DarkTooltip from '@components/core/dark-tool-tip/dark-tool-tip';
import DragHandleCell from '@components/core/drag-and-drop/drag-handle-cell';
import DraggableRow from '@components/core/drag-and-drop/draggable-row';
import { EditableGridCellEditor, EditableGridInterface } from '@components/core/editable-grid/editable-grid.interface';
import RenderFieldInput from '@components/core/editable-grid/render-field-input';
import { setWidth } from '@components/core/expandable/grid/grid.service';
import GridHeader from '@components/core/grid-header';
import GridMenu from '@components/core/grid-header/grid-menu';
import InactiveIcon from '@components/core/inactive-icon';
import { SelectWithSearchDataArrayInterface } from '@components/core/select-with-categories/select-with-categories.interface';
import { useBoolean, useTranslation } from '@hooks/index';
import './styles.scss';
import { ReorderContext } from '../grid-helper';

const EditableGrid = ({
    items,
    columns,
    title,
    setState,
    errors,
    customKey,
    setErrors,
    disabledFields,
    defineDisabledFields,
    extraMenuItems,
    className,
    editable = true,
    disableDeleteSingle = false,
    splitItemText = true,
    lineHeight,
}: EditableGridInterface) => {
    const { t } = useTranslation();
    const grid = React.useRef<HTMLDivElement | null>(null);

    const [data, setData] = useState<StudyOverviewTableConfigurationColumnConfigurations[]>([]);
    const activeItem = useRef<StudyOverviewTableConfigurationColumnConfigurations | null>(null);
    const orderChanged = useBoolean(false);
    const [currentOrderFocus, setCurrentOrderFocus] = useState<number | null>(null);
    const disableSingleRow = useBoolean(false);
    const ref = useRef(null);

    const [cursor, setCursor] = useState<number | null>(0);

    React.useEffect(() => {
        grid.current = document.querySelector(`.${customKey}`);
    }, []);

    useEffect(() => {
        setData(items ?? []);
        if (items?.length) {
            if (items?.length === 1 && disableDeleteSingle) {
                disableSingleRow.setTrue();
            } else {
                disableSingleRow.setFalse();
            }
        }
    }, [items]);

    const itemChange =
        (
            fieldName: string,
            order: number,
            validateFn?: (stateVal: StudyOverviewTableConfigurationColumnConfigurations | undefined) => void,
        ) =>
        (event: ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value;
            const newData = data.map((item) => (item.order === order ? { ...item, [fieldName]: inputValue } : item));
            const currentRowValues = newData.find((item) => item.order === order);

            if (validateFn) validateFn(currentRowValues);
            if (defineDisabledFields) defineDisabledFields(currentRowValues);

            setData([...newData]);
            if (setState) setState([...newData]);
        };

    const reorder = (dataItem: StudyOverviewTableConfigurationColumnConfigurations, direction: 'before' | 'after') => {
        if (activeItem.current === dataItem) {
            return;
        }
        const reorderedData = data.slice();
        const prevIndex = reorderedData.findIndex((p) => p === activeItem.current);
        const nextIndex = reorderedData.findIndex((p) => p === dataItem);
        const adjustIndex = direction === 'before' ? (prevIndex < nextIndex ? -1 : 0) : prevIndex < nextIndex ? 1 : 0;
        reorderedData.splice(prevIndex, 1);
        reorderedData.splice(Math.max(nextIndex + adjustIndex, 0), 0, activeItem.current || reorderedData[0]);
        orderChanged.setTrue();
        const reorderedDataArray = reorderedData.map((item, index) => ({
            ...item,
            order: index + 1,
        }));

        setData(reorderedDataArray);
        if (setState) setState(reorderedDataArray);
    };

    const dragStart = (dataItem: StudyOverviewTableConfigurationColumnConfigurations) => {
        activeItem.current = dataItem;
    };

    const deleteRow = (deletedIndex: number, e: React.MouseEvent) => {
        const dataCopy = data;
        dataCopy.splice(deletedIndex, 1);
        const orderedDataCopy = dataCopy.map((item, index) => ({
            ...item,
            order: index + 1,
        }));
        setData(orderedDataCopy);
        if (setState) setState(orderedDataCopy);
        if (setErrors) setErrors({});

        e.stopPropagation();
    };

    const checkDisabledField = (field: string, order: number) => {
        if (disabledFields?.length) {
            const fieldFoundIndex = disabledFields.findIndex((item) => item.field === field && item.order === order);

            return fieldFoundIndex !== -1;
        }

        return false;
    };

    const defineSelectValue = (
        field: string,
        selectValues:
            | ((selectField: string, order: number | null | undefined) => SelectWithSearchDataArrayInterface[])
            | SelectWithSearchDataArrayInterface[],
        cellEditor: EditableGridCellEditor | undefined,
        dataItem: StudyOverviewTableConfigurationColumnConfigurations,
    ) => {
        const value = dataItem[field];

        if (cellEditor === EditableGridCellEditor.SELECT && value) {
            const optionsArray =
                typeof selectValues === 'function' ? selectValues(field, dataItem.order) : selectValues;
            const currentValue = optionsArray?.find((option) => option.id === value);

            return currentValue?.text ?? '';
        }

        if (field !== 'name' && (dataItem.default || dataItem.name === t('admin:defaultPanelColumns'))) {
            return ' ';
        }

        return value;
    };

    const getScrollAvailable = () => {
        const scrollableContainer = grid.current?.querySelector('.k-grid-content');
        if (scrollableContainer) {
            return scrollableContainer.scrollHeight - scrollableContainer.clientHeight;
        }
    };

    return (
        <div className={cn('editable-grid mb-20', customKey, className)} key={customKey} ref={ref}>
            <GridHeader
                menu={<GridMenu extraMenuItems={extraMenuItems} hideFilter />}
                pager={false}
                title={title}
                totalRecords={data?.length}
            />
            <ReorderContext.Provider value={{ reorder, dragStart }}>
                <DragAndDrop>
                    <Grid
                        scrollable="scrollable"
                        data={data}
                        className="reorder-grid"
                        dataItemKey="order"
                        rows={{
                            data: (rowProps) => (
                                <DraggableRow
                                    {...rowProps}
                                    lineHeight={lineHeight}
                                    scrollAvailable={getScrollAvailable()}
                                    onClick={() => {
                                        if (
                                            !data[rowProps.dataIndex]?.inEdit &&
                                            editable &&
                                            rowProps.dataItem.name !== t('admin:defaultPanelColumns')
                                        ) {
                                            const copyData = data;
                                            copyData[rowProps.dataIndex].inEdit = true;
                                            copyData.forEach((item: any, index: number) => {
                                                if (
                                                    index !== rowProps.dataIndex &&
                                                    item.name !== null &&
                                                    item.type !== null
                                                ) {
                                                    item.inEdit = false;
                                                }
                                            });
                                            setData([...copyData]);
                                            if (defineDisabledFields) defineDisabledFields(rowProps.dataItem, true);
                                        }
                                    }}
                                />
                            ),
                        }}
                        editField="inEdit"
                    >
                        <GridColumn
                            width={45}
                            locked
                            cells={{
                                data: (props: GridCellProps) => (
                                    <td
                                        className="delete-row"
                                        onClick={(e: React.MouseEvent) =>
                                            disableSingleRow.value || props.dataItem.default
                                                ? null
                                                : deleteRow(props.dataIndex, e)
                                        }
                                        data-testid="delete-row"
                                    >
                                        <span
                                            className={cn('trash-icon', {
                                                disabled: props.dataItem.default,
                                            })}
                                        >
                                            <FontAwesomeIcon icon={['far', 'trash']} className="icon" />
                                        </span>
                                    </td>
                                ),
                            }}
                        />
                        <GridColumn
                            width={55}
                            locked
                            title={t('ra:order')}
                            cells={{
                                data: (props: GridCellProps) =>
                                    DragHandleCell({
                                        ...props,
                                        firstCol: true,
                                    }),
                            }}
                        />
                        {columns?.map((column) => {
                            const width = column.minWidth
                                ? setWidth(column.minWidth, column.field, ref.current, columns, 110)
                                : '';

                            return (
                                <GridColumn
                                    editable
                                    key={column.id}
                                    field={column.field}
                                    title={column.label}
                                    width={width}
                                    cells={{
                                        data: (props: GridCellProps) =>
                                            props.dataItem.inEdit ? (
                                                <td>
                                                    <RenderFieldInput
                                                        value={props.dataItem[column.field]}
                                                        column={column}
                                                        dataItem={props.dataItem}
                                                        currentOrderFocus={currentOrderFocus}
                                                        cursor={cursor}
                                                        errors={errors}
                                                        setErrors={setErrors}
                                                        addNewAction={props.dataItem.addNew}
                                                        splitItemText={splitItemText}
                                                        disabled={
                                                            checkDisabledField(column.field, props.dataItem.order) ||
                                                            !!column.disabled
                                                        }
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                            if (column.cellEditor === EditableGridCellEditor.TEXT)
                                                                setCursor(e.currentTarget.selectionStart);

                                                            setCurrentOrderFocus(
                                                                column.cellEditor === EditableGridCellEditor.TEXT
                                                                    ? props.dataItem.order
                                                                    : null, // need for input focus
                                                            );

                                                            itemChange(
                                                                column.field,
                                                                props.dataItem.order,
                                                                column?.validateFn,
                                                            )(e);
                                                        }}
                                                    />
                                                </td>
                                            ) : (
                                                <td>
                                                    {column.showInactiveIcon &&
                                                        column.showInactiveIcon(props.dataItem) && (
                                                            <DarkTooltip title={t('inactive')} placement="top" arrow>
                                                                <span>
                                                                    <InactiveIcon />
                                                                </span>
                                                            </DarkTooltip>
                                                        )}
                                                    {defineSelectValue(
                                                        column.field,
                                                        column.selectValues ?? [],
                                                        column.cellEditor,
                                                        props.dataItem,
                                                    ) || <span className="na-text">{t('ra:n/a')}</span>}{' '}
                                                </td>
                                            ),
                                    }}
                                />
                            );
                        })}
                    </Grid>
                </DragAndDrop>
            </ReorderContext.Provider>
        </div>
    );
};

export default EditableGrid;
